/**
 * The thread as live mode draws it (docs/plans/os-voice-conversation.md,
 * C10, step 3.10): the kept messages and the live transcript, one list keyed
 * by the server's ids.
 *
 * - A kept message is drawn where it stands. A `system` message is drawn only
 *   when it is live mode's divider ("Live mode ended · m:ss[ · reason]",
 *   `liveEndedOf`), which the thread keeps under `${callId}-end`, or the
 *   "Now in <name>." it leaves when it carries on after a setup's Create
 *   (`liveNowInOf`), kept under `${callId}-now-in`.
 * - A transcript row (`you` or `os`) is drawn until the thread keeps a
 *   message under its id, then the message takes its place: a row is
 *   replaced, never drawn beside its message.
 * - An acknowledgement, the spoken gist of an answer, a line that only fills
 *   a wait ("Still waiting…") and a line with no words are heard, never
 *   drawn or kept (J10 as revised by Devendra on 7 and 12 Oct,
 *   `isThreadLine`), stored before then or not.
 * - A reply a correction replaced is marked: its own `metadata.replaced`, or
 *   a `replaced` frame naming the person's line it answered.
 *
 * Finished live lines join the thread as messages under the same ids
 * (`liveMessageOf`, `upsertMessages`), so a call that ends, or a call on
 * another thread, leaves them where they were said; the server keeps them
 * under the same ids (2.10), so a reopened thread draws the same lines.
 *
 * Pure, so `bun test` reads every rule.
 */
import {
  AGENT_LIVE_ENDED_PART,
  AGENT_LIVE_NOW_IN_PART,
  inSpokenOrder,
  isThreadLine,
  liveEndedOf,
  liveNowInOf,
  orderStampOf,
  replacedMetadataOf,
  voiceMetadataOf,
  type AgentLiveEndedData,
  type LiveLineKind,
  type LiveRow,
  type LiveTranscript,
  type LiveVia,
} from '@alluzo/shared';

/** A message as the thread reads it. */
export type ThreadMessage = {
  readonly id: string;
  readonly role: string;
  readonly parts: readonly unknown[];
  readonly metadata?: unknown;
};

export type LiveThreadItem<M extends ThreadMessage> =
  | { readonly kind: 'message'; readonly id: string; readonly message: M; readonly replaced: boolean }
  | { readonly kind: 'divider'; readonly id: string; readonly data: AgentLiveEndedData }
  | { readonly kind: 'now_in'; readonly id: string; readonly name: string }
  | { readonly kind: 'you'; readonly id: string; readonly text: string; readonly partial: boolean; readonly via: LiveVia }
  | { readonly kind: 'os'; readonly id: string; readonly text: string; readonly streaming: boolean; readonly voice: LiveLineKind; readonly replyTo?: string };

export type LiveThreadInput<M extends ThreadMessage> = {
  readonly messages: readonly M[];
  /** The live transcript of the call up on this thread; none without one. */
  readonly transcript: LiveTranscript | null;
  /** Ids drawn elsewhere. */
  readonly hidden?: ReadonlySet<string>;
};

/**
 * The replies a correction replaced: each that says so itself, and every
 * reply after a person's line a `replaced` frame names, up to their next line.
 */
const replacedRepliesOf = (messages: readonly ThreadMessage[], replaced: Readonly<Record<string, string>>): ReadonlySet<string> => {
  const out = new Set<string>();
  let underReplaced = false;
  for (const message of messages) {
    if (message.role === 'user') underReplaced = Object.hasOwn(replaced, message.id);
    else if (message.role === 'assistant' && (underReplaced || replacedMetadataOf(message.metadata) !== null)) out.add(message.id);
  }
  return out;
};

const rowItemOf = <M extends ThreadMessage>(row: LiveRow): LiveThreadItem<M> | null => {
  if (row.role === 'you') return row.text.trim() === '' ? null : { kind: 'you', id: row.id, text: row.text, partial: row.partial, via: row.via };
  if (row.role === 'os')
    return row.text.trim() === '' || !isThreadLine(row.kind, row.text)
      ? null
      : { kind: 'os', id: row.id, text: row.text, streaming: row.streaming, voice: row.kind, ...(row.replyTo === undefined ? {} : { replyTo: row.replyTo }) };
  return null;
};

/**
 * The thread in the order things happened (spoken-order.ts; the 7 Oct fix
 * list, item 1): kept messages, then the rows still arriving, sorted by the
 * server's stamps (`metadata.at`, a row's `at`), so a line kept late, or an
 * answer that landed while OS spoke, never swaps places with what came
 * before it. What has no stamp keeps its place, and the person's words still
 * being said are always last.
 */
export const liveThreadOf = <M extends ThreadMessage>({ messages, transcript, hidden = new Set() }: LiveThreadInput<M>): LiveThreadItem<M>[] => {
  const replaced = replacedRepliesOf(messages, transcript?.replaced ?? {});
  const kept = new Set(messages.map((message) => message.id));
  const entries: { readonly item: LiveThreadItem<M>; readonly at: number | null }[] = [];
  for (const message of messages) {
    if (hidden.has(message.id)) continue;
    const at = orderStampOf(message.metadata);
    if (message.role === 'system') {
      const data = liveEndedOf(message);
      const nowIn = liveNowInOf(message);
      if (data !== null) entries.push({ item: { kind: 'divider', id: message.id, data }, at });
      else if (nowIn !== null) entries.push({ item: { kind: 'now_in', id: message.id, name: nowIn.name }, at });
      continue;
    }
    // An acknowledgement, a gist, filler or a line with no words is heard, never drawn: a thread stored before 7 Oct still holds acknowledgements, one before 12 Oct gists.
    const voice = message.role === 'assistant' ? voiceMetadataOf(message.metadata) : null;
    if (voice !== null && voice.kind !== 'user' && !isThreadLine(voice.kind, textOf(message))) continue;
    entries.push({ item: { kind: 'message', id: message.id, message, replaced: replaced.has(message.id) }, at });
  }
  for (const row of transcript?.rows ?? []) {
    if (kept.has(row.id) || hidden.has(row.id)) continue;
    const item = rowItemOf<M>(row);
    if (item === null || row.role === 'kept') continue;
    entries.push({ item, at: row.role === 'you' && row.partial ? Number.POSITIVE_INFINITY : (row.at ?? null) });
  }
  return inSpokenOrder(entries, (entry) => entry.at).map((entry) => entry.item);
};

/** A finished live line, as the message the thread keeps under its id (the server keeps the same, 2.10). Null while it is still arriving. */
export const liveMessageOf = (row: LiveRow): ThreadMessage | null => {
  if (row.role === 'you') {
    if (row.partial || row.text.trim() === '') return null;
    return {
      id: row.id,
      role: 'user',
      parts: [{ type: 'text', text: row.text }],
      metadata: {
        ...(row.via === 'voice' ? { spoken: true } : {}),
        voice: { kind: 'user' },
        ...(row.at === undefined ? {} : { at: row.at }),
      },
    };
  }
  if (row.role === 'os') {
    if (row.streaming || row.text.trim() === '') return null;
    return {
      id: row.id,
      role: 'assistant',
      parts: [{ type: 'text', text: row.text }],
      metadata: {
        spoken: true,
        voice: { kind: row.kind, ...(row.replyTo === undefined ? {} : { replyTo: row.replyTo }) },
        ...(row.at === undefined ? {} : { at: row.at }),
      },
    };
  }
  return null;
};

/** A message's text, its text parts joined. */
const textOf = (message: ThreadMessage): string =>
  message.parts
    .flatMap((part) => (typeof part === 'object' && part !== null && (part as { type?: unknown }).type === 'text' ? [String((part as { text?: unknown }).text ?? '')] : []))
    .join('');

/**
 * The thread with these messages put in by id: one it holds is replaced where
 * it stands (a kept turn takes its live line's place; a line the server wrote
 * again in Roman letters takes its own), one it does not hold goes after the
 * rest. The same array when nothing changed. While a typed turn streams, its
 * reply at the end stays last, as the AI SDK writes it in place only there.
 * `lineOnly`: the incoming are live lines, which change only the words of a
 * message already held and keep the rest of it.
 */
export const upsertMessages = <M extends ThreadMessage>(held: readonly M[], incoming: readonly M[], { typedInFlight = false, lineOnly = false } = {}): readonly M[] => {
  let next: M[] | null = null;
  const fresh: M[] = [];
  const at = new Map(held.map((message, index) => [message.id, index]));
  for (const message of incoming) {
    const index = at.get(message.id);
    if (index === undefined) {
      if (!fresh.some((one) => one.id === message.id)) fresh.push(message);
      continue;
    }
    const was = (next ?? held)[index] as M;
    if (lineOnly) {
      if (textOf(was) === textOf(message)) continue;
      next ??= [...held];
      next[index] = { ...was, parts: message.parts };
      continue;
    }
    if (was === message) continue;
    next ??= [...held];
    next[index] = message;
  }
  if (fresh.length === 0) return next ?? held;
  const base = next ?? [...held];
  const last = base[base.length - 1];
  if (typedInFlight && last?.role === 'assistant') return [...base.slice(0, -1), ...fresh, last];
  return [...base, ...fresh];
};

/** The divider live mode leaves as it ends, as the thread keeps it (the server's `${callId}-end`, 2.10). */
export const liveDividerMessageOf = (id: string, data: AgentLiveEndedData): ThreadMessage => ({
  id,
  role: 'system',
  parts: [{ type: AGENT_LIVE_ENDED_PART, data }],
});

/** The divider live mode leaves as it carries on after a setup's Create, as the thread keeps it (the server's `${callId}-now-in`, step 5.2). */
export const liveNowInMessageOf = (callId: string, name: string): ThreadMessage => ({
  id: `${callId}-now-in`,
  role: 'system',
  parts: [{ type: AGENT_LIVE_NOW_IN_PART, data: { name } }],
});
